import { Injectable, Injector } from '@angular/core';
import { createEffect, ofType, Actions } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { map, mergeMap, tap, type Observable } from 'rxjs';
import { Store, type Action } from '@ngrx/store';
import type { HeatpumpsFunnelSettingsInterface } from '@innogy/feh/models';
import type { ScLinkField } from '@core/jss-models';
import { openGenericModal } from '@innogy/common-ui-modals';
import {
  GenericModalPosition,
  type GenericModalProps,
} from '@legacy/common-ui-shared-interfaces';
import { TrackToolService } from '@analytics/events';
import type {
  MultipleAnswerQuestion,
  QuestionnaireTarget,
} from '@feh/shared-ui';

import {
  showHeatpumpsQuestionnaireNextQuestionAction,
  submitHeatpumpsQuestionnaireSingleAnswerAction,
  submitHeatpumpsQuestionnaireMultipleAnswerAction,
  openHeatpumpsQuestionnaireInfoModal,
  leaveHeatpumpsQuestionnaireAction,
} from './heatpumps-questionnaire.actions';
import {
  onHeatpumpsFunnelNavigationAction,
  parsedheatpumpsFunnelToolTrackingConfig,
  selectHeatpumpsFunnelSettings,
} from '../heatpumps-funnel';
import { selectHeatpumpsCurrentQuestion } from './heatpumps-questionnaire.selectors';

@Injectable()
export class HeatpumpsQuestionnaireEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly injector: Injector
  ) {}

  handleSingleAnswerSubmit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitHeatpumpsQuestionnaireSingleAnswerAction),
      concatLatestFrom(() => this.store.select(selectHeatpumpsFunnelSettings)),
      mergeMap(([action, funnelSettings]) =>
        this.mapNavigateAction(action.answer.target, funnelSettings)
      )
    )
  );

  handleMultipleAnswerSubmit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitHeatpumpsQuestionnaireMultipleAnswerAction),
      concatLatestFrom(() => [
        this.store.select(selectHeatpumpsFunnelSettings),
        this.selectMultipleAnswerQuestion$(),
      ]),
      mergeMap(([action, funnelSettings, currentQuestion]) =>
        action.answers.reduce((acc, cur) => acc + cur.value, 0) >=
        currentQuestion.valueNeeded
          ? this.mapNavigateAction(
              currentQuestion.goodAnswer.target,
              funnelSettings
            )
          : this.mapNavigateAction(
              currentQuestion.badAnswer.target,
              funnelSettings
            )
      )
    )
  );

  handleLeaveQuestionnaireAction$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(leaveHeatpumpsQuestionnaireAction),
        tap(() => {
          /**
           * WTA-17: resolve injector context
           */
          const trackToolService = this.injector.get(TrackToolService);
          trackToolService.completeTool(
            parsedheatpumpsFunnelToolTrackingConfig
          );
        })
      ),
    { dispatch: false }
  );

  private mapNavigateAction(
    target: QuestionnaireTarget,
    funnelSettings: HeatpumpsFunnelSettingsInterface
  ): Action[] {
    switch (target.type) {
      case 'next-question':
        return [
          showHeatpumpsQuestionnaireNextQuestionAction({
            next: target.questionName,
          }),
        ];

      case 'link':
        return [
          onHeatpumpsFunnelNavigationAction({
            page: funnelSettings[
              target.funnelKey as keyof typeof funnelSettings
            ] as unknown as ScLinkField,
          }),
          leaveHeatpumpsQuestionnaireAction(),
        ];

      case 'info-modal':
      default:
        return [
          openHeatpumpsQuestionnaireInfoModal(
            target as { funnelKey: keyof typeof funnelSettings }
          ),
        ];
    }
  }

  private selectMultipleAnswerQuestion$(): Observable<MultipleAnswerQuestion> {
    return this.store
      .select(selectHeatpumpsCurrentQuestion)
      .pipe(
        map((question) =>
          question.type === 'multiple-answer-question'
            ? question
            : (null as any)
        )
      );
  }

  public readonly openInfoModal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(openHeatpumpsQuestionnaireInfoModal),
      concatLatestFrom(() => this.store.select(selectHeatpumpsFunnelSettings)),
      map(([info, funnelSettings]) => {
        const modalData = funnelSettings[
          info.funnelKey as keyof typeof funnelSettings
        ] as unknown as GenericModalProps;
        return openGenericModal({
          ...modalData,
          position: GenericModalPosition.LEFT,
        });
      })
    )
  );
}
